import * as THREE from 'three';

const MIN_BULGE = 0.08;
const MAX_BULGE = 0.5;

/**
 * Projects a point radially onto the glass sphere's surface (optionally lifted
 * above it), so nodes and paths sit on top of the sphere instead of inside it.
 */
export function projectOntoSphere(
  point: THREE.Vector3,
  center: THREE.Vector3,
  radius: number,
  lift = 0,
): THREE.Vector3 {
  const dir = point.clone().sub(center);
  if (dir.lengthSq() < 1e-8) {
    dir.set(0, 1, 0);
  }
  return dir.normalize().multiplyScalar(radius + lift).add(center);
}

/**
 * Quadratic bezier from start to end whose control point is pushed radially
 * outward from sphereCenter, so the curve bulges toward the glass sphere's
 * surface instead of cutting a straight chord through it.
 */
export function buildSphereArc(
  start: THREE.Vector3,
  end: THREE.Vector3,
  sphereCenter: THREE.Vector3,
  sphereRadius: number,
): THREE.QuadraticBezierCurve3 {
  const mid = start.clone().add(end).multiplyScalar(0.5);
  const radial = mid.clone().sub(sphereCenter);
  const radialLen = radial.length();
  const segmentLen = start.distanceTo(end);

  if (radialLen < 1e-6 || sphereRadius < 1e-6) {
    return new THREE.QuadraticBezierCurve3(start.clone(), mid, end.clone());
  }

  const bulgeRatio = THREE.MathUtils.clamp(segmentLen / sphereRadius, MIN_BULGE, MAX_BULGE);
  const targetLen = radialLen + sphereRadius * bulgeRatio;
  const control = radial.normalize().multiplyScalar(targetLen).add(sphereCenter);

  return new THREE.QuadraticBezierCurve3(start.clone(), control, end.clone());
}

/** Position + orientation (aligned to curve tangent) at parameter t, for instancing along an arc. */
export function curvePointMatrix(
  curve: THREE.QuadraticBezierCurve3,
  t: number,
  target: THREE.Matrix4,
): void {
  const pos = curve.getPointAt(t);
  const tangent = curve.getTangentAt(t);
  const holeAxis = new THREE.Vector3(0, 0, 1);
  const quat = new THREE.Quaternion().setFromUnitVectors(holeAxis, tangent);
  target.compose(pos, quat, new THREE.Vector3(1, 1, 1));
}
